import { ACTIVE_LIST_KEY, listKeyForId, listKeyOf } from './normalize';

/**
 * The legacy half of the bridge (docs/local-first-food-data.md, "Legacy → food"): the answers of the
 * calendar's and meal ideas' online-only write endpoints, as a food `changes`. The food reducer
 * merges them into what it holds, so a write made through a legacy endpoint is in the persisted
 * copy without waiting for the next snapshot.
 *
 * The action types are written out rather than imported: their modules import the store, and the
 * food reducer is part of building it. FoodLegacyChangesTest dispatches the real constants.
 *
 * Meals and slot notes are written as ops since phase 4, so no meal answer comes through here.
 */

/** The echoed budget of a legacy answer, as a number, or null when it has none. */
const budgetOf = (value) => (value === null || value === undefined || value === '' ? null : Number(value));

/**
 * A grocery list endpoint's answer, `lists` in their legacy shape (items inside, struck rows on the
 * active list with their bought list's id), as changes. Each row is keyed by the list it is on; a
 * row the answer no longer has on a list it answered for is deleted, unless the answer put it
 * elsewhere.
 */
export const listAnswerChanges = (entities, lists) => {
	const upsertLists = [];
	const upsertRows = [];
	const lastBoughtByName = {};
	const answeredKeys = new Set();
	const placed = new Set();
	for(const list of lists) {
		if(!list) {
			continue;
		}
		const key = listKeyOf(list);
		const { items, dates, ...fields } = list;
		upsertLists.push({ ...fields, key });
		answeredKeys.add(key);
		for(const item of items || []) {
			const { struck, last_bought: lastBought, ...row } = item;
			const ownKey = struck ? listKeyForId(entities, item.grocery_list_id, lists) : key;
			if(!ownKey) {
				continue;
			}
			upsertRows.push({ ...row, list_key: ownKey });
			placed.add(Number(row.id));
			if(key === ACTIVE_LIST_KEY && !struck && lastBought && row.name) {
				lastBoughtByName[String(row.name).toLowerCase()] = lastBought;
			}
		}
	}
	const deleteRows = Object.values(entities.groceryRows)
		.filter((row) => answeredKeys.has(row.list_key) && !placed.has(Number(row.id)))
		.map((row) => Number(row.id));
	const changes = { upsert: { grocery_lists: upsertLists, grocery_rows: upsertRows }, delete: { grocery_rows: deleteRows } };
	if(Object.keys(lastBoughtByName).length) {
		changes.upsert.last_bought_by_name = lastBoughtByName;
	}
	return changes;
};

/**
 * Meal ideas and groups from an answer in meal_idea/index's shape, which is the whole of both: ones
 * the store holds that it lacks are deleted.
 */
const ideasChanges = (entities, index) => {
	const ideaIds = new Set((index.ideas || []).map((idea) => Number(idea.id)));
	const groupIds = new Set((index.groups || []).map((group) => Number(group.id)));
	return {
		upsert: { meal_ideas: index.ideas || [], meal_idea_groups: index.groups || [] },
		delete: {
			meal_ideas: Object.keys(entities.mealIdeas).map(Number).filter((id) => !ideaIds.has(id)),
			meal_idea_groups: Object.keys(entities.mealIdeaGroups).map(Number).filter((id) => !groupIds.has(id)),
		},
	};
};

/**
 * `{ budgetId, changes }` for a legacy action the food store takes, or null for any other. The
 * budget is the one the server echoed, for the reducer to compare with its own; an answer without
 * one is dropped there.
 */
export const legacyChanges = (action, entities) => {
	const payload = action.payload || {};
	switch(action.type) {
		case 'ADD_MEAL_IDEA_TO_GROCERY_LIST_SUCCESS':
		case 'REMOVE_MEAL_IDEA_INGREDIENT_FROM_GROCERY_LIST_SUCCESS':
			return { budgetId: budgetOf(payload.budgetId), changes: listAnswerChanges(entities, payload.groceryLists || []) };
		case 'SAVE_GROCERY_LIST_SUCCESS':
			return { budgetId: budgetOf(payload.budgetId), changes: listAnswerChanges(entities, [payload.groceryListResponse]) };
		case 'CALENDAR_EVENT_SAVED':
			return { budgetId: budgetOf(payload.budgetId), changes: { upsert: { calendar_events: [payload.event] } } };
		case 'MEAL_IDEAS_LOADED':
			return { budgetId: budgetOf(payload.budget_id), changes: ideasChanges(entities, payload) };
		default:
			return null;
	}
};
